// #8187: where login may send a signed-in user. `?return=` used to accept ANY absolute http(s) URL, so
// https://admin.pagemind.ai/login?return=https://evil.example/ was an open redirect from our own domain.
//
// Allowed: a path on THIS origin, or an absolute URL on this origin, or an https URL on one of the explicit external
// origins below (the simulator on onboarding.pagemind.ai, which sends logged-out users here with ?return=<its URL>,
// pagemind-onboarding-real/app/simulator/[siteId]/page.tsx). Everything else is REFUSED with a reason; the caller
// logs it and goes to /dashboard. There is no "best effort" rewrite of a refused target.
export const EXTERNAL_RETURN_ORIGINS: readonly string[] = ['https://onboarding.pagemind.ai'];
// Security review of PR #8213 (M2): a session hand-off code is minted for an external return target automatically, so
// only the page that consumes it may be one. Any other onboarding page would leave the code to its analytics scripts.
const EXTERNAL_RETURN_PATH = /^\/simulator\/[A-Za-z0-9-]+\/?$/;

export type ReturnTarget =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; href: string }
  | { kind: 'refused'; reason: string };

export function resolveReturnTarget(raw: string | null | undefined, currentOrigin: string): ReturnTarget | null {
  if (raw == null || raw === '') return null;
  // Browsers treat a backslash like a slash and ignore tabs/newlines inside URLs, so "/\evil.example" and "/\t/evil"
  // would leave the origin. Refuse them outright instead of trying to normalise.
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return { kind: 'refused', reason: 'contains a backslash or control character' };
  let url: URL;
  try {
    url = new URL(raw, currentOrigin);
  } catch {
    return { kind: 'refused', reason: 'not a valid URL' };
  }
  if (url.origin === currentOrigin) {
    // A relative target must be a real path: "//evil.example" is protocol-relative and already resolved to another
    // origin above; anything else that is not "/..." (e.g. "dashboard", "javascript:...") is refused.
    if (!/^https?:\/\//i.test(raw) && !raw.startsWith('/')) return { kind: 'refused', reason: 'a relative target must start with /' };
    // codex #8211 r1 (both reviewers, blocking): "https://<this origin>//evil.example/x" and "/.//evil.example/x" are
    // this origin, but their normalised pathname "//evil.example/x" is protocol-relative: handed to the router it
    // names ANOTHER origin (the browser throws SecurityError on replaceState). Refuse it like any other origin.
    if (url.pathname.startsWith('//')) return { kind: 'refused', reason: 'the path would be protocol-relative (//)' };
    return { kind: 'internal', path: `${url.pathname}${url.search}${url.hash}` };
  }
  if (url.protocol === 'https:' && EXTERNAL_RETURN_ORIGINS.includes(url.origin) && !url.username && !url.password) {
    if (!EXTERNAL_RETURN_PATH.test(url.pathname)) return { kind: 'refused', reason: `only the simulator page (/simulator/<id>) may receive a sign-in, not ${url.pathname}` };
    return { kind: 'external', href: url.href };
  }
  return { kind: 'refused', reason: `origin ${url.origin} is not allowed` };
}
